import React from 'react';
import { RouteComponentProps, Route, Redirect } from 'react-router-dom';

import { concatUrl, readPathId } from 'modules/shared/utils';
import { getParentModulePath } from 'modules/shared/utils/route';
import { StructureNavigationProps } from 'modules/shared/types';
import { NotFound, NotifiedRoute, UnknownContent } from 'modules/shared/components';

import {
    HomePage,
    HomeAltPage,
    UniversePage,
    CategoryPage,
    ProductPage,
    BrandsPage,
    BrandPage,
    SignPage,
} from '../../../../pages';

export const ContentSelector: React.FC<StructureNavigationProps> = ({
    template,
    navigationTree,
}: StructureNavigationProps) => {
    switch (template) {
        case 'catalogue-landing':
            return (
                <NotifiedRoute
                    exact
                    path={navigationTree.path}
                    render={() => {
                        return <HomePage />;
                    }}
                    navigationTree={navigationTree}
                />
            );
        case 'catalogue-landing-v2':
            return (
                <NotifiedRoute
                    exact
                    path={navigationTree.path}
                    render={() => {
                        return <HomeAltPage />;
                    }}
                    navigationTree={navigationTree}
                />
            );
        case 'ProductEntry':
            return (
                <React.Fragment>
                    <Route
                        exact
                        path={concatUrl([navigationTree.path, '/:product'])}
                        render={(subProps: RouteComponentProps<{ product: string }>) => {
                            return (
                                <ProductPage
                                    id={readPathId(subProps.match.params.product)}
                                    offline={false}
                                    path={subProps.match.url}
                                    parentNavigationTree={
                                        navigationTree.parent ? navigationTree.parent : navigationTree
                                    }
                                />
                            );
                        }}
                    />
                    <Route
                        exact
                        path={concatUrl([navigationTree.path, '/:product/bo'])}
                        render={(subProps: RouteComponentProps<{ product: string }>) => {
                            return (
                                <ProductPage
                                    id={readPathId(subProps.match.params.product)}
                                    offline
                                    path={subProps.match.url}
                                    parentNavigationTree={
                                        navigationTree.parent ? navigationTree.parent : navigationTree
                                    }
                                />
                            );
                        }}
                    />
                </React.Fragment>
            );
        case 'BrandEntry':
            return (
                <React.Fragment>
                    <NotifiedRoute
                        exact
                        path={navigationTree.path}
                        component={BrandsPage}
                        navigationTree={navigationTree}
                    />
                    <Route
                        exact
                        path={concatUrl([navigationTree.path, '/:brand'])}
                        render={(subProps: RouteComponentProps<{ brand: string }>) => {
                            const pathId = readPathId(subProps.match.params.brand);
                            if (pathId === '') {
                                return <NotFound />;
                            }

                            return (
                                <BrandPage
                                    id={readPathId(subProps.match.params.brand)}
                                    filterParams={[
                                        {
                                            key: 'brandId',
                                            value: pathId,
                                        },
                                    ]}
                                    path={subProps.match.url}
                                    parentNavigationTree={navigationTree}
                                />
                            );
                        }}
                    />
                </React.Fragment>
            );

        case 'SignEntry':
            return (
                <React.Fragment>
                    <Route
                        exact
                        path={concatUrl([navigationTree.path, '/:sign'])}
                        render={(subProps: RouteComponentProps<{ sign: string }>) => {
                            const signId = subProps.match.params.sign;
                            if (signId === '') {
                                return <NotFound />;
                            }

                            return (
                                <SignPage
                                    filterParams={[
                                        {
                                            key: 'signId',
                                            value: signId.toUpperCase(),
                                        },
                                    ]}
                                    path={subProps.match.url}
                                    parentNavigationTree={navigationTree}
                                />
                            );
                        }}
                    />
                    <Route
                        exact
                        path={navigationTree.path}
                        render={() => {
                            return (
                                <React.Fragment>
                                    {navigationTree.parent && (
                                        <Redirect from={navigationTree.path} to={navigationTree.parent.path} />
                                    )}
                                </React.Fragment>
                            );
                        }}
                    />
                </React.Fragment>
            );
        // Defaut (les univers/catégorie/sous-catégorie)
        default: {
            const parentModulePath = getParentModulePath(navigationTree);
            if (parentModulePath) {
                return (
                    <React.Fragment>
                        <Route
                            exact
                            path={concatUrl([parentModulePath, '/:universe/:category/:subcategory'])}
                            render={(subProps: RouteComponentProps<{ subcategory: string }>) => {
                                const pathId = readPathId(subProps.match.params.subcategory);
                                if (pathId === '') {
                                    return <NotFound />;
                                }

                                return (
                                    <CategoryPage
                                        id={pathId}
                                        path={subProps.match.url}
                                        parentNavigationTree={
                                            navigationTree.parent ? navigationTree.parent : navigationTree
                                        }
                                    />
                                );
                            }}
                        />
                        <Route
                            exact
                            path={concatUrl([parentModulePath, '/:universe/:category'])}
                            render={(subProps: RouteComponentProps<{ category: string }>) => {
                                const pathId = readPathId(subProps.match.params.category);
                                if (pathId === '') {
                                    return <NotFound />;
                                }

                                return (
                                    <CategoryPage
                                        id={pathId}
                                        path={subProps.match.url}
                                        parentNavigationTree={
                                            navigationTree.parent ? navigationTree.parent : navigationTree
                                        }
                                    />
                                );
                            }}
                        />
                        <Route
                            exact
                            path={concatUrl([parentModulePath, '/:universe'])}
                            render={(subProps: RouteComponentProps<{ universe: string }>) => {
                                const pathId = readPathId(subProps.match.params.universe);
                                if (pathId === '') {
                                    return <NotFound />;
                                }

                                return (
                                    <UniversePage
                                        filterParams={[
                                            {
                                                key: 'universeId',
                                                value: pathId,
                                            },
                                        ]}
                                        path={subProps.match.url}
                                        parentNavigationTree={
                                            navigationTree.parent ? navigationTree.parent : navigationTree
                                        }
                                    />
                                );
                            }}
                        />
                        <Route
                            exact
                            path={navigationTree.path}
                            render={() => {
                                return (
                                    <React.Fragment>
                                        {navigationTree.parent && (
                                            <Redirect from={navigationTree.path} to={navigationTree.parent.path} />
                                        )}
                                    </React.Fragment>
                                );
                            }}
                        />
                    </React.Fragment>
                );
            }
            return <UnknownContent />;
        }
    }
};
